import { Injectable } from '@angular/core'

import { CiecRequestAttempt, CiecRequestOutcome } from '@data/interfaces/ciec-request.interface'
import { CIEC_MIN_LENGTH } from '@data/constants/ciec.constant'

/**
 * Estructura tipada del rationale parseado de `offerExplanation[]`.
 *
 * Permite renderizar cada línea como una "factor card" con icono por categoría,
 * inputs separados visualmente y output (factor o monto) destacado.
 * Origen de las strings: `api-rendafin/src/modules/verification/services/verification.service.ts:171-180`.
 */
export interface ParsedRationale {
  kind: 'capacity' | 'score' | 'employees' | 'blacklist' | 'customer_concentration' | 'supplier_concentration' | 'margin' | 'composite' | 'unknown'
  icon: string
  title: string
  inputValue?: { label: string; value: string }
  inputs?: Array<{ label: string; value: string; icon: string }>
  output: { label: string; value: string; isAmount: boolean; isFactor: boolean }
  raw: string
}

/**
 * Qué mostrarle al usuario interno cuando el análisis fiscal no se pudo completar.
 *
 * `action` es lo que separa un aviso de algo útil: dice qué puede hacer el usuario desde donde
 * está, y el componente la traduce a un botón o a abrir el modal correspondiente.
 */
export interface CiecFailureInfo {
  severity: 'error' | 'warn' | 'info'
  summary: string
  detail: string
  action: CiecFailureAction
}

/**
 * Qué puede hacer el usuario interno con un fallo (B-075). Cada pantalla decide si el valor se vuelve
 * botón (`edit-rfc` → «Editar cliente», `generate-new` → «Enviar enlace nuevo») o solo texto
 * (`confirm-sat`, `notify-it`, `wait`).
 */
export type CiecFailureAction = 'request-ciec' | 'edit-rfc' | 'retry' | 'generate-new' | 'notify-it' | 'wait' | 'confirm-sat' | 'none'

/** Severidades de `p-tag`. `info` se pinta navy en este tema (ver docs/design/design-system.md). */
export type CiecTagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary'

/** Etiqueta de una solicitud o de un intento, lista para un `p-tag`. `hint` = qué hacer, si hay algo que hacer. */
export interface CiecLabel {
  label: string
  severity: CiecTagSeverity
  hint?: string
}

//* Fallos que no son del cliente sino del proveedor o nuestros: al cliente y al panel se les cuentan igual.
const PROVIDER_SIDE_CODES = new Set(['SYNTAGE_UNAVAILABLE', 'SYNTAGE_CREDENTIAL_ERROR', 'VERIFICATION_FAILED'])

/**
 * Traducciones y formateadores para los datos del análisis CIEC/Syntage.
 *
 * Centraliza el diccionario que antes vivía inline en `lease-algorithm.component.ts`,
 * para reuso entre `lease-algorithm` (snapshot del lease) y la nueva pestaña
 * "Análisis Fiscal CIEC" del `customer-detail` (vigente del cliente). ADR-0031.
 *
 * Reglas:
 *   - Si una key no está mapeada, devolver el texto original (humanizado en
 *     `translateMetricKey` haciendo Title Case de snake_case).
 *   - `translateRationale` opera por substring replace global — útil para los
 *     `rationale[]` que Syntage emite como texto libre con etiquetas inglesas.
 */
@Injectable({ providedIn: 'root' })
export class CiecTranslateService {
  // ─── source ──────────────────────────────────────────────────────────────
  /**
   * Cómo se disparó la verificación.
   *
   * ! Esto NO dice **quién** la pidió: no hay usuario guardado en la verificación, solo el camino
   * ! por el que entró. La etiqueta de la ficha dice «Se pidió:» y no «Solicitado por:» justo por
   * ! eso — la segunda promete un nombre que el dato no tiene.
   *
   * Los valores arrancan en minúscula y con preposición porque se leen pegados a esa etiqueta,
   * como una sola frase: «Se pidió: desde el panel, por el equipo interno».
   */
  private readonly sources: Record<string, string> = {
    //* El endpoint síncrono. Ningún frontend lo consume hoy: si aparece, entró por la API a pelo.
    'verify-ciec': 'por la API, directamente',
    //* El camino del cotizador público, cuando el prospecto teclea su CIEC sin tener sesión.
    'verify-ciec-async': 'desde el canal digital, por el cliente',
    'manual-backoffice': 'desde el panel, por el equipo interno',
    revalidation: 'al revalidar con la CIEC guardada',
    backfill: 'en la migración de datos históricos',
    'ekatena-import': 'al importar el análisis de Ekatena',
    manual: 'a mano',
    api: 'por la API, directamente',
    'snapshot-empty': 'sin verificación previa',
  }
  translateSource(source: string | null | undefined): string {
    //* «Desconocido» a secas leía mal pegado a la etiqueta —«Se pidió: Desconocido»—. Y un camino
    //* sin traducir se devuelve tal cual: prefiero que se vea la llave cruda a inventar un texto.
    if (!source) return 'por un camino que no quedó registrado'
    return this.sources[source] || source
  }

  // ─── status ──────────────────────────────────────────────────────────────
  private readonly statuses: Record<string, string> = {
    success: 'Exitoso',
    error: 'Error',
    pending: 'Pendiente',
    processing: 'Procesando',
    failed: 'Fallido',
    missing: 'Sin información',
  }
  translateStatus(status: string | null | undefined): string {
    if (!status) return 'Desconocido'
    return this.statuses[status] || status
  }

  // ─── message ─────────────────────────────────────────────────────────────
  private readonly messages: Record<string, string> = {
    'Verification successful': 'Verificación exitosa',
    'Verification failed': 'Verificación fallida',
    'Invalid RFC or CIEC credentials': 'RFC o CIEC inválidos',
  }
  translateMessage(message: string | null | undefined): string {
    if (!message) return ''
    return this.messages[message] || message
  }

  // ─── decision (lease snapshot) ───────────────────────────────────────────
  private readonly decisions: Record<string, string> = {
    approved_in_range: 'Aprobado en rango',
    approved_with_difference: 'Aprobado con diferencia',
    approved_below_range: 'Aprobado bajo rango',
    rejected_no_range: 'Rechazado',
  }
  translateDecision(decision: string | null | undefined): string {
    if (!decision) return '—'
    return this.decisions[decision] || decision
  }

  private readonly rejectionReasons: Record<string, string> = {
    insufficient_data: 'Datos fiscales insuficientes',
    persona_fisica: 'Solo financiamos personas morales',
  }
  translateRejectionReason(reason: string | null | undefined): string {
    if (!reason) return ''
    return this.rejectionReasons[reason] || reason
  }

  // ─── score source ────────────────────────────────────────────────────────
  private readonly scoreSources: Record<string, string> = {
    syntage: 'Syntage',
    buro: 'Buró de Crédito',
    circle: 'Círculo de Crédito',
    ekatena: 'Ekatena',
    internal: 'Interno',
  }
  translateScoreSource(source: string | null | undefined): string {
    if (!source) return ''
    const key = source.toLowerCase()
    return this.scoreSources[key] || source.toUpperCase()
  }

  // ─── score type ──────────────────────────────────────────────────────────
  private readonly scoreTypes: Record<string, string> = {
    internal: 'Interno',
    external: 'Externo',
    bureau: 'Buró',
  }
  translateScoreType(type: string | null | undefined): string {
    if (!type) return ''
    return this.scoreTypes[type.toLowerCase()] || type
  }

  // ─── classification A/B/C/D ──────────────────────────────────────────────
  classifyByScore(score: number | null | undefined): string {
    if (score == null || isNaN(score)) return 'N/A'
    if (score >= 750) return 'A'
    if (score >= 650) return 'B'
    if (score >= 550) return 'C'
    return 'D'
  }

  // ─── métricas (ratios y campos calculados) ───────────────────────────────
  //* Llaves tal como llegan en `financial_ratios` de Syntage (F-158 completó las que se pintaban en inglés). Es solo la
  //* etiqueta: ningún consumidor usa el texto traducido como llave de datos.
  private readonly metricKeys: Record<string, string> = {
    // Liquidez
    current_ratio: 'Liquidez corriente',
    quick_ratio: 'Liquidez inmediata',
    cash_ratio: 'Liquidez de caja',
    working_capital: 'Capital de trabajo',
    acid_test: 'Prueba ácida',
    cash_to_assets: 'Efectivo / activos',
    net_working_capital_to_sales: 'Capital de trabajo neto / ventas',
    // Rentabilidad
    return_on_assets: 'Retorno sobre activos (ROA)',
    return_on_equity: 'Retorno sobre capital (ROE)',
    return_on_sales: 'Retorno sobre ventas (ROS)',
    gross_margin: 'Margen bruto',
    net_margin: 'Margen neto',
    profit_margin: 'Margen de utilidad',
    gross_profit_margin: 'Margen de utilidad bruta',
    return_on_investment: 'Rendimiento sobre la inversión (ROI)',
    // Apalancamiento
    total_debt_ratio: 'Endeudamiento total',
    debt_equity_ratio: 'Deuda / Capital',
    debt_ratio: 'Endeudamiento',
    solvency_ratio: 'Solvencia',
    capital_intensity: 'Intensidad de capital',
    interest_to_sales: 'Intereses / Ventas',
    solvency: 'Solvencia',
    capital_intensity_ratio: 'Intensidad de capital',
    interest_expense_to_sales: 'Gastos por intereses / ventas',
    //* Va en apalancamiento junto a los gastos por intereses y sale vacía cuando no hay intereses que cubrir: es la
    //* cobertura de intereses (utilidad operativa / gastos por intereses).
    coverage: 'Cobertura de intereses',
    degree_of_financial_leverage: 'Grado de apalancamiento financiero',
    // Actividad
    total_asset_turnover: 'Rotación de activos',
    sales_to_capital: 'Ventas / Capital',
    days_sales_outstanding: 'DSO (días)',
    days_payable_outstanding: 'DPO (días)',
    working_capital_turnover: 'Rotación capital de trabajo',
    fixed_asset_turnover_ratio: 'Rotación de activos fijos',
    sales_to_capital_employed: 'Ventas / capital empleado',
    inventory_turnover: 'Rotación de inventarios',
    days_inventory_outstanding: 'Días de inventario',
    accounts_receivable_collection_period: 'Periodo promedio de cobro',
    accounts_payable_payment_period: 'Periodo promedio de pago',
    cash_conversion_cycle: 'Ciclo de conversión de efectivo',
    // Crecimiento y otros
    sales_growth_percentage: 'Crecimiento de ventas (%)',
    ingresos_netos: 'Ingresos netos',
  }
  translateMetricKey(key: string): string {
    if (!key) return ''
    if (this.metricKeys[key]) return this.metricKeys[key]
    // Fallback: snake_case → Title Case
    return key
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }

  // ─── valores cualitativos en strings Syntage ─────────────────────────────
  private readonly metricValues: Record<string, string> = {
    'Insufficient data to calculate annual sales growth': 'Datos insuficientes para calcular el crecimiento anual',
    'Annual sales < 10,000,000': 'Ventas anuales menores a $10,000,000',
    'Quick ratio between 1.2 and 3': 'Liquidez inmediata entre 1.2 y 3',
    'Current ratio between 1.2 and 3': 'Liquidez corriente entre 1.2 y 3',
    'Debt ratio below 0.5': 'Endeudamiento por debajo de 0.5',
    'Good profit margins': 'Buenos márgenes de utilidad',
  }
  translateMetricValue(value: string | null | undefined): string {
    if (!value) return ''
    return this.metricValues[value] || value
  }

  // ─── listas negras ───────────────────────────────────────────────────────
  private readonly blacklistFlags: Record<string, string> = {
    pep: 'PEP',
    sanciones: 'Sanciones',
    ofac: 'OFAC',
    issued: 'Emitidas',
    received: 'Recibidas',
  }
  translateBlacklistFlag(flag: string): string {
    return this.blacklistFlags[flag.toLowerCase()] || flag.toUpperCase()
  }

  // ─── rationale (substring replace global) ────────────────────────────────
  private readonly rationaleReplacements: Array<[RegExp, string]> = [
    [/Base capacity chosen from/g, 'Capacidad base elegida del'],
    [/max\(/g, 'máximo('],
    [/netIncome\*6=/g, 'utilidad×6 meses='],
    [/annualSales\*0\.25=/g, 'ventas anuales×25%='],
    [/avgBilling\*0\.35=/g, 'facturación promedio×35%='],
    [/cashFlow\*0\.3=/g, 'cash flow×30%='],
    [/Score=/g, 'Score='],
    [/factor=/g, 'factor='],
    [/Employees=/g, 'Empleados='],
    [/sizeFactor=/g, 'factor de tamaño='],
    [/Blacklist=/g, 'Lista negra='],
    [/Top customer share=/g, 'Mayor cliente='],
    [/Top supplier share=/g, 'Mayor proveedor='],
    [/Margin=/g, 'Margen='],
    [/Composite factor=/g, 'Factor compuesto='],
    [/maxAmount=/g, 'monto máximo='],
    [/Higher capacity due to strong margins and revenues/g, 'Capacidad mayor por buenos márgenes y facturación'],
  ]
  translateRationale(text: string | null | undefined): string {
    if (!text) return ''
    let out = text
    for (const [re, replacement] of this.rationaleReplacements) {
      out = out.replace(re, replacement)
    }
    return out
  }

  // ─── parser de offerExplanation[] → ParsedRationale[] ────────────────────
  //
  // Los rationales que emite el backend tienen 8 patrones deterministic
  // (verification.service.ts:171-180). Parseamos sobre el string inglés
  // original para que la estructura sea estable frente a cambios de
  // traducción.
  parseOfferExplanation(lines: string[] | null | undefined): ParsedRationale[] {
    if (!Array.isArray(lines)) return []
    return lines.map((l) => this.parseRationaleLine(String(l || '')))
  }

  parseRationaleLine(line: string): ParsedRationale {
    const raw = String(line || '').trim()

    // 1. Capacidad base (4 fuentes + máximo)
    const capRe =
      /^Base capacity chosen from max\(netIncome\*6=(-?\d+(?:\.\d+)?), annualSales\*0\.25=(-?\d+(?:\.\d+)?), avgBilling\*0\.35=(-?\d+(?:\.\d+)?), cashFlow\*0\.3=(-?\d+(?:\.\d+)?)\)\s*=>\s*(-?\d+(?:\.\d+)?)/i
    const capM = raw.match(capRe)
    if (capM) {
      const [, netInc, annSales, avgBill, cashFlow, baseCap] = capM
      return {
        kind: 'capacity',
        icon: 'fa-coins',
        title: 'Capacidad base',
        inputs: [
          { label: 'Utilidad × 6 meses', value: this.formatCurrencyCompact(Number(netInc)), icon: 'fa-coins' },
          { label: 'Ventas × 25%', value: this.formatCurrencyCompact(Number(annSales)), icon: 'fa-arrow-trend-up' },
          {
            label: 'Facturación × 35%',
            value: this.formatCurrencyCompact(Number(avgBill)),
            icon: 'fa-file-invoice-dollar',
          },
          { label: 'Cash flow × 30%', value: this.formatCurrencyCompact(Number(cashFlow)), icon: 'fa-arrows-up-down' },
        ],
        output: {
          label: 'Máximo',
          value: this.formatCurrencyCompact(Number(baseCap)),
          isAmount: true,
          isFactor: false,
        },
        raw,
      }
    }

    // 2. Score crediticio → factor
    const scoreM = raw.match(/^Score=(-?\d+(?:\.\d+)?)\s*→\s*factor=(-?\d+(?:\.\d+)?)/i)
    if (scoreM) {
      return {
        kind: 'score',
        icon: 'fa-gauge-high',
        title: 'Score crediticio',
        inputValue: { label: 'Score', value: scoreM[1] + ' pts' },
        output: { label: 'factor', value: Number(scoreM[2]).toFixed(2), isAmount: false, isFactor: true },
        raw,
      }
    }

    // 3. Empleados → sizeFactor
    const empM = raw.match(/^Employees=(-?\d+(?:\.\d+)?)\s*→\s*sizeFactor=(-?\d+(?:\.\d+)?)/i)
    if (empM) {
      return {
        kind: 'employees',
        icon: 'fa-users',
        title: 'Tamaño de la empresa',
        inputValue: { label: 'Empleados', value: empM[1] },
        output: { label: 'factor tamaño', value: Number(empM[2]).toFixed(2), isAmount: false, isFactor: true },
        raw,
      }
    }

    // 4. Listas negras → factor
    //* `Blacklist=false` solo dice que ninguna lista que el algoritmo lee bloqueó la oferta, no que se haya
    //* consultado todo y salido limpio: por eso no se escribe «Limpio» (F-158).
    const blM = raw.match(/^Blacklist=(true|false)\s*→\s*factor=(-?\d+(?:\.\d+)?)/i)
    if (blM) {
      const flagged = blM[1].toLowerCase() === 'true'
      return {
        kind: 'blacklist',
        icon: 'fa-shield-halved',
        title: 'Listas restringidas',
        inputValue: { label: 'Estado', value: flagged ? 'Detectado' : 'Sin listas que bloqueen la oferta' },
        output: { label: 'factor', value: Number(blM[2]).toFixed(2), isAmount: false, isFactor: true },
        raw,
      }
    }

    // 5. Concentración cliente → factor
    const cliM = raw.match(/^Top customer share=(-?\d+(?:\.\d+)?)%\s*→\s*factor=(-?\d+(?:\.\d+)?)/i)
    if (cliM) {
      return {
        kind: 'customer_concentration',
        icon: 'fa-user-tie',
        title: 'Concentración de clientes',
        inputValue: { label: 'Mayor cliente', value: Number(cliM[1]).toFixed(1) + '%' },
        output: { label: 'factor', value: Number(cliM[2]).toFixed(2), isAmount: false, isFactor: true },
        raw,
      }
    }

    // 6. Concentración proveedor → factor
    const provM = raw.match(/^Top supplier share=(-?\d+(?:\.\d+)?)%\s*→\s*factor=(-?\d+(?:\.\d+)?)/i)
    if (provM) {
      return {
        kind: 'supplier_concentration',
        icon: 'fa-truck',
        title: 'Concentración de proveedores',
        inputValue: { label: 'Mayor proveedor', value: Number(provM[1]).toFixed(1) + '%' },
        output: { label: 'factor', value: Number(provM[2]).toFixed(2), isAmount: false, isFactor: true },
        raw,
      }
    }

    // 7. Margen → factor
    const margM = raw.match(/^Margin=(-?\d+(?:\.\d+)?)%\s*→\s*factor=(-?\d+(?:\.\d+)?)/i)
    if (margM) {
      return {
        kind: 'margin',
        icon: 'fa-percent',
        title: 'Margen operativo',
        inputValue: { label: 'Margen', value: Number(margM[1]).toFixed(1) + '%' },
        output: { label: 'factor', value: Number(margM[2]).toFixed(2), isAmount: false, isFactor: true },
        raw,
      }
    }

    // 8. Factor compuesto → monto máximo
    const compM = raw.match(/^Composite factor=(-?\d+(?:\.\d+)?)\s*→\s*maxAmount=(-?\d+(?:\.\d+)?)/i)
    if (compM) {
      return {
        kind: 'composite',
        icon: 'fa-scale-balanced',
        title: 'Factor compuesto',
        inputValue: { label: 'Multiplicador', value: '× ' + Number(compM[1]).toFixed(3) },
        output: {
          label: 'Monto máximo',
          value: this.formatCurrencyCompact(Number(compM[2])),
          isAmount: true,
          isFactor: false,
        },
        raw,
      }
    }

    // Fallback: no matchea ninguno de los 8 patrones conocidos
    return {
      kind: 'unknown',
      icon: 'fa-circle-info',
      title: '',
      output: { label: '', value: '', isAmount: false, isFactor: false },
      raw,
    }
  }

  // ─── formatters ──────────────────────────────────────────────────────────
  /**
   * Pesos en `es-MX`. Sin decimales por defecto (montos del análisis); `decimals` para quien sí los necesite.
   */
  formatCurrency(value: number | null | undefined, decimals = 0): string {
    if (value == null || isNaN(value as any)) return 'N/A'
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value as number)
  }

  formatCurrencyCompact(value: number | null | undefined): string {
    if (value == null || isNaN(value as any)) return 'N/A'
    const n = Math.abs(value as number)
    if (n >= 1_000_000) return '$' + ((value as number) / 1_000_000).toFixed(1) + 'M'
    if (n >= 1_000) return '$' + ((value as number) / 1_000).toFixed(0) + 'k'
    return this.formatCurrency(value)
  }

  formatPercent(value: number | null | undefined, decimals = 2): string {
    if (value == null || isNaN(value as any)) return 'N/A'
    return new Intl.NumberFormat('es-MX', {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format((value as number) / 100)
  }

  formatRatio(value: number | null | undefined): string {
    if (value == null || isNaN(value as any)) return 'N/A'
    return Number(value).toFixed(2)
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return 'N/A'
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  formatDateTime(date: string | Date | null | undefined): string {
    if (!date) return 'N/A'
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  formatDays(value: number | null | undefined): string {
    if (value == null || isNaN(value as any)) return 'N/A'
    return Math.round(value as number) + ' días'
  }

  /**
   * Pago mensual estimado bajo sistema francés (cuotas iguales).
   * Fórmula: M = P × (i/12) × (1+i/12)^n / ((1+i/12)^n − 1)
   * - `amount` en MXN, `rateAnnual` como porcentaje (ej. 28.2 = 28.2%), `months` en meses.
   * - Devuelve `null` si faltan datos o si la tasa es <=0 / plazo es <=0.
   *
   * Es una estimación referencial para que el comité dimensione la cuota a
   * primera vista; no reemplaza la tabla de amortización oficial del lease.
   */
  estimateMonthlyPayment(amount: number | null | undefined, rateAnnual: number | null | undefined, months: number | null | undefined): number | null {
    const P = Number(amount)
    const r = Number(rateAnnual)
    const n = Number(months)
    if (!P || P <= 0 || isNaN(P)) return null
    if (!n || n <= 0 || isNaN(n)) return null
    if (r == null || isNaN(r) || r < 0) return null
    const i = r / 12 / 100
    if (i === 0) return P / n
    const factor = Math.pow(1 + i, n)
    return (P * i * factor) / (factor - 1)
  }

  /**
   * "Vigente hasta DD MMM YYYY" o "Expiró hace N días" según `expiresAt`.
   */
  formatValidityHint(expiresAt: string | Date | null | undefined): string {
    if (!expiresAt) return ''
    const d = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt
    const now = Date.now()
    const delta = d.getTime() - now
    const dayMs = 24 * 60 * 60 * 1000
    if (delta >= 0) return `Vigente hasta ${this.formatDate(d)}`
    const daysAgo = Math.floor(Math.abs(delta) / dayMs)
    if (daysAgo === 0) return 'Expiró hoy'
    if (daysAgo === 1) return 'Expiró hace 1 día'
    if (daysAgo < 30) return `Expiró hace ${daysAgo} días`
    const monthsAgo = Math.floor(daysAgo / 30)
    return `Expiró hace ${monthsAgo} mes${monthsAgo > 1 ? 'es' : ''}`
  }

  /**
   * Describe un fallo del análisis a partir del `code` que manda el backend.
   *
   * Los códigos son el contrato; el texto vive aquí porque el mismo fallo se le cuenta distinto al
   * usuario interno que al cliente final —el interno puede corregir el RFC del expediente o pedir
   * una CIEC nueva, el cliente no—. Antes todos los fallos caían en el mismo toast genérico
   * («No se pudo ejecutar la verificación CIEC»), que no dice ni qué pasó ni qué sigue.
   */
  describeFailure(code: string | null | undefined, message?: string | null, satReason?: string | null): CiecFailureInfo {
    switch (code) {
      case 'CIEC_NOT_STORED':
        return { severity: 'info', summary: 'Falta la CIEC del cliente', detail: 'El cliente no tiene CIEC guardada. Genera una solicitud para que la ingrese.', action: 'request-ciec' }
      case 'CIEC_MISSING':
        return { severity: 'warn', summary: 'Falta la CIEC', detail: 'Escribe la CIEC del cliente para correr el análisis.', action: 'none' }
      case 'CIEC_INVALID':
        //* El SAT nunca la vio: no tiene el formato de una CIEC. El error más común es teclear la
        //* contraseña de la e.firma, que es otra cosa.
        return {
          severity: 'warn',
          summary: 'La CIEC no tiene un formato válido',
          detail: `Debe tener al menos ${CIEC_MIN_LENGTH} caracteres. Revisa que no sea la contraseña de la e.firma.`,
          action: 'none',
        }
      case 'CIEC_REJECTED': {
        //* El SAT rechazó la CIEC con que se corrió: la guardada —deja de servir cuando el cliente la
        //* cambia— o la que el usuario capturó a mano. No hay nada que reintentar: hay que pedirle la vigente.
        const base = message || 'La CIEC que tenemos ya no es válida. Solicítale al cliente la vigente.'
        return { severity: 'warn', summary: 'El SAT rechazó la CIEC', detail: satReason ? `${base} Motivo del SAT: «${satReason}».` : base, action: 'request-ciec' }
      }
      case 'RFC_MISSING':
      case 'RFC_INVALID':
        return { severity: 'warn', summary: 'Revisa el RFC del cliente', detail: message || 'El RFC del cliente no tiene un formato válido.', action: 'edit-rfc' }
      case 'CIEC_REQUEST_IN_PROGRESS':
        //* 409 al generar o reenviar: el cliente está enviando su CIEC justo ahora. Reemplazarle el
        //* enlace a media validación tiraría su intento, por eso el servidor no lo permite.
        return {
          severity: 'info',
          summary: 'El cliente está enviando su CIEC',
          detail: message || 'El cliente está enviando su CIEC en este momento. Espera un par de minutos y vuelve a intentarlo.',
          action: 'wait',
        }
      case 'CIEC_REQUEST_EXHAUSTED':
        return {
          severity: 'warn',
          summary: 'Intentos agotados',
          detail: 'El SAT rechazó la CIEC tres veces en este enlace. Confirma con el cliente su CIEC vigente antes de enviarle uno nuevo.',
          action: 'generate-new',
        }
      case 'SUBMIT_IN_PROGRESS':
        return { severity: 'info', summary: 'Envío en proceso', detail: 'Ya hay un envío de la CIEC en proceso. Espera a que termine.', action: 'wait' }
      case 'SYNTAGE_CREDENTIAL_ERROR':
        //* El proveedor rechazó la consulta por algo que no es la CIEC (permisos, plan, configuración).
        return {
          severity: 'error',
          summary: 'Error de nuestro lado',
          detail: message || 'El proveedor rechazó la consulta por un problema de configuración. No es la CIEC del cliente: avisa a TI.',
          action: 'notify-it',
        }
      case 'SYNTAGE_NO_DATA':
        //* Las credenciales sirvieron: el problema es que el SAT no tiene movimientos con qué
        //* calcular. Reintentar hoy da lo mismo, así que se dice explícitamente.
        return {
          severity: 'warn',
          summary: 'Sin datos suficientes',
          detail: message || 'El SAT no devolvió información suficiente para estimar capacidad. Reintentar ahora daría el mismo resultado.',
          action: 'none',
        }
      case 'SYNTAGE_UNAVAILABLE':
        return {
          severity: 'error',
          summary: 'Syntage no disponible',
          detail: message || 'No se pudo consultar la información fiscal. Es del proveedor, no del cliente: vuelve a intentarlo en unos minutos.',
          action: 'retry',
        }
      case 'VERIFICATION_FAILED':
        return {
          severity: 'error',
          summary: 'El análisis no se completó',
          detail: message || 'Falló de nuestro lado, no por la CIEC del cliente. Reintenta en unos minutos; si se repite, avisa a TI.',
          action: 'notify-it',
        }
      default:
        return { severity: 'error', summary: 'Error', detail: message || 'No se pudo ejecutar la verificación CIEC', action: 'retry' }
    }
  }

  // ─── solicitudes de CIEC por enlace (B-075) ──────────────────────────────
  //* Única fuente de las etiquetas del historial. El `outcome` lo calcula el servidor; aquí solo se
  //* traduce. Un valor que el panel no conozca se muestra tal cual en gris en vez de romper la tabla.
  private readonly requestOutcomes: Record<CiecRequestOutcome, CiecLabel> = {
    pending: { label: 'Pendiente', severity: 'info' },
    pending_rejected: { label: 'Pendiente · con rechazos', severity: 'warning' },
    received: { label: 'Recibida', severity: 'success' },
    received_pending_sat: { label: 'Recibida · SAT sin confirmar', severity: 'info' },
    exhausted: { label: 'Intentos agotados', severity: 'danger' },
    expired: { label: 'Vencida', severity: 'secondary' },
    cancelled: { label: 'Cancelada', severity: 'secondary' },
    legacy_rejected: { label: 'Rechazada por el SAT', severity: 'danger' },
    legacy_error: { label: 'Error de nuestro lado', severity: 'danger' },
    //* Las solicitudes anteriores a B-075 que el sistema dio por «usadas» aunque el SAT nunca validó
    //* la CIEC: el análisis que dejaron salió sin credencial y en $0.
    legacy_no_credential: { label: 'Usada sin credencial del SAT', severity: 'danger' },
    legacy_failed: { label: 'Falló', severity: 'danger' },
    used_unknown: { label: 'Usada · sin detalle', severity: 'secondary' },
  }

  describeRequestOutcome(outcome: string | null | undefined): CiecLabel {
    if (!outcome) return { label: '—', severity: 'secondary' }
    return this.requestOutcomes[outcome as CiecRequestOutcome] ?? { label: outcome, severity: 'secondary' }
  }

  /** Etiqueta de un envío del cliente por el enlace, según su `code`. */
  describeAttempt(attempt: Pick<CiecRequestAttempt, 'code' | 'counted' | 'credentialStatus' | 'late'> | null | undefined): CiecLabel {
    if (!attempt) return { label: '—', severity: 'secondary' }
    const code = attempt.code
    if (code === null || code === undefined) {
      return attempt.credentialStatus === 'pending' ? { label: 'Recibida · SAT sin confirmar', severity: 'info' } : { label: 'Recibida', severity: 'success' }
    }
    if (code === 'CIEC_REJECTED') {
      const hint = 'Que el cliente confirme su CIEC en el portal del SAT'
      if (attempt.late === true) return { label: 'Rechazada por el SAT (tardío)', severity: 'danger', hint }
      //* Un rechazo que no contó es la misma CIEC ya rechazada: el servidor no la vuelve a mandar al SAT.
      return attempt.counted ? { label: 'Rechazada por el SAT', severity: 'danger', hint } : { label: 'Repitió la CIEC ya rechazada', severity: 'warning', hint }
    }
    if (code === 'CIEC_INVALID') return { label: 'Formato inválido · ¿escribió la e.firma?', severity: 'warning' }
    if (code === 'RFC_MISSING' || code === 'RFC_INVALID') return { label: 'RFC inválido', severity: 'warning', hint: 'Corregir el RFC en «Editar cliente»' }
    if (PROVIDER_SIDE_CODES.has(code)) return { label: 'Error de nuestro lado', severity: 'danger', hint: 'Puede reintentar; si se repite, avisar a TI' }
    return { label: code, severity: 'secondary' }
  }

  /** `true` si el fallo es del proveedor o nuestro (no de la CIEC ni del RFC del cliente). */
  isProviderSideCode(code: string | null | undefined): boolean {
    return !!code && PROVIDER_SIDE_CODES.has(code)
  }
}
